/**
 * THE ONE WAY THIS SPA LEAVES ITS OWN ORIGIN, and it is a top-level browser
 * navigation -- never `fetch`, never an XHR, never an iframe.
 *
 * WHY IT IS A MODULE OF ITS OWN, one function long (plan 3b amendment A1).
 * `POST /admin-api/<provider>/oauth-start` no longer answers the provider's
 * consent URL. `authorizeUrl` now carries a HANDOFF url on our own public API
 * origin -- `GET /admin-api/<provider>/oauth-redirect?s=<state JWT>` -- which
 * mints 32 random bytes, hands them to THE BROWSER THAT ASKED as
 * `__Host-oauth_bind`, stores only their SHA-256 against the state row, and
 * only then 302s to Google/eBay. The provider's callback parks nothing unless
 * that same browser presents the preimage (fail closed, including when the
 * stored hash is NULL -- api/src/adminRoutes.ts's `handleOAuthCallback` on
 * `phase3/oauth-bind`).
 *
 * All of which is worth nothing if the SPA reaches that URL with `fetch`: the
 * cookie would land on an XHR the user's browser never navigates with, the
 * consent would then happen in a browser holding no binding, and the callback
 * would refuse every legitimate connect. Top-level navigation IS the
 * mechanism, not a stylistic preference about how to leave a page.
 *
 * SEPARATE MODULE so `vi.mock('../src/lib/navigation')` can intercept it: an
 * ES module's INTERNAL call to its own function is not interceptable, so a
 * navigation helper living next to `startProviderConnect` could not be
 * observed by any test at all -- and jsdom refuses to let a spy redefine
 * `window.location.assign` (it is [LegacyUnforgeable], verified in this
 * package). Without this file, "did the connect navigate or did it fetch?"
 * would be a question no test in this repo could ask.
 */

/**
 * Refuses anything but `http:`/`https:`. The URL comes from our own BFF over a
 * same-origin request, so this is not a trust boundary -- it is the one line
 * that stops a `javascript:` URL from ever reaching `location.assign`, where
 * it would execute as script ON THIS ORIGIN. `http:` is allowed because a
 * local `PUBLIC_API_BASE` is http; every deployed environment is https.
 */
export function isNavigableUrl(url: string): boolean {
  try {
    const protocol = new URL(url).protocol;
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Returns false without navigating when the URL is not navigable.
 *
 * `target` EXISTS FOR TESTS AND FOR NOTHING ELSE, and no production call site
 * passes it (review MAJ-1). Both suites replaced this whole module with a mock,
 * which meant `window.location.assign(url)` -- the single line the entire
 * browser-binding mitigation rests on -- was never executed by any test: the
 * reviewer rewrote this body to `fetch(url, { credentials: 'include' })` and the
 * full gate stayed green. The parameter makes the real body callable from Node.
 *
 * IT IS NOT A HOLE A CALL SITE COULD HIDE IN, and the reason is stronger than
 * "a spy would notice" (Task 11 re-review, which attacked exactly this by
 * passing a fetching target from a production call site -- killed by two
 * tests). DOM suite #1 drives the whole connect through the REAL module with
 * `window.location` itself replaced, and asserts that `assign` was called WITH
 * THE HANDOFF URL. Satisfying that assertion requires actually navigating: a
 * substituted target cannot reach the window-level spy, and a mutant that
 * called both would still have navigated, which is the property.
 *
 * And since the re-review, the seam is not merely argued safe but UNREACHABLE:
 * eslint.config.js's `NO_TEST_ONLY_SEAM` makes a two-argument call an error
 * outside `test/`, where the rule is off.
 */
export function assignTopLevel(
  url: string,
  target: Pick<Location, 'assign'> = window.location,
): boolean {
  if (!isNavigableUrl(url)) return false;
  target.assign(url);
  return true;
}
